import { FC } from 'react'
import { Plus, X as Close } from 'lucide-react'
import { ChatInner } from '../../chat/ChatInner'
import { useIntl } from 'react-intl'
import { useAuthStore } from '../../../../store/authStore'
import { useFreeChat } from '../../chat/useFreeChat'
import '../../chat/chat.scss'
const DrawerMessenger: FC = () => {
  const intl = useIntl()
  /**
   * The finding this drawer is currently open on, for the header subtitle.
   *
   * `aiAssistant` is a MAP keyed by the finding id — the same key `ChatInner`
   * reads it back under, and the same one `SetAiAssistantVulnerabilitiesID`
   * writes. This used to read `aiAssistant.vulnerabilitiesSelected`, a key
   * nothing has ever written, so `title` was permanently `undefined` and the
   * subtitle below never rendered for any finding. Read the live binding
   * instead: `selectedVulnId` is what picks the entry.
   *
   * Still tolerant of a miss — `selectedVulnId` is `''` with nothing selected,
   * and the subtitle is conditional — so this only ever adds a line that was
   * already designed for, never removes the header.
   */
  const title = useAuthStore((state) => state.aiAssistant?.[state.selectedVulnId]?.title)
  /**
   * The header action appears only once a free chat is actually BOUND.
   *
   * With nothing bound the body is already showing the "Ask the AI anything"
   * start screen, whose "Start a chat" button does exactly this — two controls
   * for one action, one of them redundant. The header button exists to escape a
   * RESUMED conversation, so it belongs only where there is one to escape.
   */
  const isFreeMode = useAuthStore((state) => state.aiEntryPoint === 'free')
  const hasBoundChat = useAuthStore(
    (state) => !!state.aiAssistant?.[state.selectedVulnId]?.id,
  )
  const showNewChat = isFreeMode && hasBoundChat
  const { startNewChat, isCreating } = useFreeChat()
  return (
    <div
      className='modal fade'
      id='kt_modal_AI'
      style={{ backdropFilter: 'blur(3px)' }}
      role='dialog'
      aria-labelledby='kt_modal_AI_title'
      data-bs-backdrop='static'
      tabIndex={-1}
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      <div className='modal-dialog modal-dialog-centered' style={{ maxWidth: '900px', maxHeight: "80vh", height: "700px" }}>

        <div className='lum-modal shadow-lg border-0 h-100 modal-content' dir='ltr'>

          <div className='lum-modal-header'>
            <div className='lum-orb'>✦</div>
            <div className='d-flex flex-column'>
              <span className='lum-modal-title'>{intl.formatMessage({ id: "AI Assistant" })}</span>
              {title && <span className='lum-modal-sub'>{title}</span>}
            </div>
            {showNewChat && (
              /*
                The explicit escape from "resume the last free chat". Without it
                the only route to a fresh conversation is to finish or abandon
                the one the drawer picked on the user's behalf.
              */
              <button
                type='button'
                data-testid='drawer-new-chat'
                className='lum-modal-new'
                disabled={isCreating}
                onClick={() => startNewChat()}
              >
                <Plus size={14} aria-hidden='true' />
                <span>{intl.formatMessage({ id: 'CHAT.FREE.NEW_CHAT' })}</span>
              </button>
            )}
            <button
              type='button'
              className='lum-modal-close'
              aria-labelledby='kt_modal_AI_title'
              data-bs-toggle='modal'
              data-bs-target='#kt_modal_AI'
              onClick={() => {
                useAuthStore.getState().SetAiAssistantVulnerabilitiesID({
                  vulID: null,
                  id: null,
                  title: null
                })
                useAuthStore.getState().SetSelectedVulnId("")
                // The store is global: a stale 'free' would follow the user to
                // the finding page and suppress its "pick a vulnerability" gate.
                useAuthStore.getState().SetAiEntryPoint('finding')
              }}
            >
              <Close size={22} />
            </button>
          </div>
          <div className='modal-body p-0' style={{ height: "calc(100% - 56px)" }}>

            <ChatInner isDrawer={true} />
          </div>
        </div>
      </div>
    </div>
  )
}

export { DrawerMessenger }
